import {
	ISubmitedCampaignResponse,
	ISubmitedQuestResponse,
	IUserStatisticsResponse
} from '@services/users/users.services.type';
import jwt_decode from 'jwt-decode';
import { IChest } from 'src/features/character/features/characterChests/components/ChestSection/ChestSection.type';
import { EQuestType } from 'src/features/devHub/features/devHubSingleQuest/components/DevHubSingleQuestPage/DevHubSingleQuestPage.enum';
import { isTestEnv } from 'src/functions/functions';
import { ISubmitedQuest, IUserStatistics } from 'src/interfaces/user';
import { ESkillType, ETree } from 'src/styles/theme/utils/enums';
import { getEnumFromValue } from 'src/styles/theme/utils/functions';
import { defaultSkillsProgress, defaultUserStatistics } from './userStore.variables';

// format data fetched from axiosClient.get('/users/statistics') into IUserStatistics
function formatUserStatistics(fetchedUserStatistics: IUserStatisticsResponse['data']['data']) {
	try {
		const {
			gold,
			goldCollected,
			dust,
			inventory,
			campaignCount,
			campaignSubmiteds,
			questCount,
			questSubmiteds,
			skillsProgress,
			sumExpPoints,
			guardianSince,
			rankNumber
		} = fetchedUserStatistics;

		const formattedUserData = {
			skillsProgress: {
				...defaultSkillsProgress,
				level: skillsProgress?.level,
				sumExpPoints: sumExpPoints
			}
		} as IUserStatistics;

		// set skillsProgress data
		Object.entries(skillsProgress).forEach(([key, value]: [string, any]) => {
			if (key === ETree.cairo || key === ETree.solidity || key === ETree.general) {
				const exps: { [key: string]: number } = value.exp;

				Object.entries(exps).forEach(([expKey, expValue]: [string, number]) => {
					const skill = getEnumFromValue(expKey, ESkillType);

					if (skill) {
						formattedUserData.skillsProgress[key][skill] = expValue;
					}
				});
			}
		});

		// set submitedQuests data
		formattedUserData.submitedQuests = questSubmiteds
			.filter((submitedQuest: ISubmitedQuestResponse) => !!submitedQuest.quest)
			.map((submitedQuest: ISubmitedQuestResponse): ISubmitedQuest => {
				const { id, parts, quest, finishedAt } = submitedQuest;

				const { id: questId, title, slug, type, difficulty } = quest;
				const { technicalID, id: typeId, label: typeLabel } = type || {};

				return {
					id,
					slug,
					difficulty,
					questId,
					title,
					type: {
						id: typeId,
						label: typeLabel,
						type: getEnumFromValue(technicalID || EQuestType.unset, EQuestType)
					},
					parts,
					isFinished: !!finishedAt
				};
			});

		// set submitedCampaigns data
		formattedUserData.submitedCampaigns = campaignSubmiteds
			.filter((submitedCampaign: ISubmitedCampaignResponse) => !!submitedCampaign.campaign)
			.map((submitedCampaign: ISubmitedCampaignResponse) => {
				const { id, campaign, finishedAt } = submitedCampaign;

				const { id: campaignId, title, slug } = campaign;

				return {
					id,
					slug,
					campaignId,
					title,
					isFinished: !!finishedAt
				};
			});

		if (inventory) {
			const { common, uncommon, rare, epic, legendary, unique, total, itemsCollected, itemsReforged, itemsSold } =
				inventory.items;

			// set inventory data
			formattedUserData.inventory = {
				chests: inventory.chests.map(
					({ chest, count }): IChest => ({
						id: chest.id,
						count
					})
				),
				items: {
					common,
					uncommon,
					rare,
					epic,
					legendary,
					unique,
					total,
					itemsCollected,
					itemsReforged,
					itemsSold
				}
			};
		}

		const completedQuests = formattedUserData.submitedQuests.filter(
			(submitedQuest: any) => !!submitedQuest.isFinished
		).length;

		const completedCampaigns = formattedUserData.submitedCampaigns.filter(
			(submitedCampaign: any) => !!submitedCampaign.isFinished
		).length;

		formattedUserData.gold = gold;
		formattedUserData.goldCollected = goldCollected;
		formattedUserData.dust = dust;
		formattedUserData.campaignCount = campaignCount;
		formattedUserData.questCount = questCount;
		formattedUserData.guardianSince = new Date(guardianSince);
		formattedUserData.rankNumber = rankNumber;
		formattedUserData.startedQuests = questSubmiteds.length - completedQuests;
		formattedUserData.completedQuests = completedQuests;
		formattedUserData.completedCampaigns = completedCampaigns;

		return formattedUserData;
	} catch (err) {
		console.log("Can't format user statistics data", err);

		return defaultUserStatistics;
	}
}

function checkIfJWTIsValid(token?: string) {
	if (!token) return false;

	if (isTestEnv()) {
		return true;
	}

	try {
		// lifetime of the jwt
		const timeLimit = Math.floor(Date.now() / 1000) + 12 * 60 * 60;

		const newJwtDecode: {
			// expiration timestamp
			exp: number;
			// start timestamp
			iat: number;
			// user id
			id: number;
		} = jwt_decode(token || '');

		return newJwtDecode.exp > timeLimit;
	} catch {}

	return false;
}

export { checkIfJWTIsValid, formatUserStatistics };
